import API from './api'

const {REACT_APP_API_URI} = process.env;

const updateProperty = (subject, keyProperty, valueProperty, collection, defaultValue) => {
    if(typeof defaultValue == "undefined") {
        defaultValue = null;
    }
    const key = subject[keyProperty];
    if(key && collection[key]) {
        subject[valueProperty] = collection[key]
    } else {
        subject[valueProperty] = defaultValue;
    }
    return subject
}

const normalizeBooleanStatus = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'active', 'yes', 'enabled'].includes(normalized)) return true;
        if (['false', '0', 'inactive', 'no', 'disabled'].includes(normalized)) return false;
    }
    return false;
};

const formatUsers = (response) => {
    // The response structure is {data: {...}, version: '1.0.0', ...}
    // So we need to access response.data.data, not response.data.data.data
    if (!response || !response.data) {
        console.warn('Invalid response structure:', response);
        return [];
    }

    const data = response.data.data || response.data; // Try both structures
    let users = data.users ? Object.values(data.users) : [];
  
    return users.map((user) => {
        // Map the new structure to expected format
        const formattedUser = {
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            phone: user.phone || '',
            status: normalizeBooleanStatus(user.is_active ?? user.status),
            is_superuser: user.is_superuser,
            profile_image: user.profile_image,
            created_at: user.created_at,
            updated_at: user.updated_at,
            // Handle networks array
            networks: user.networks || []
        };
        
        // If user has networks, use the first one for role info
        if (user.networks && user.networks.length > 0) {
            const firstNetwork = user.networks[0];
            formattedUser.role = {
                id: firstNetwork.role_id,
                name: firstNetwork.role_name
            };
            formattedUser.tenant_name = firstNetwork.tenant_name;
            formattedUser.membership_status = firstNetwork.status;
        }
        
        return formattedUser;
    });
}

const formatMembers = (response) => {
    if (!response || !response.data) {
        console.warn('Invalid response structure:', response);
        return [];
    }

    const data = response.data.data || response.data;
    
    // The members are already in array format
    let members = [];
    if (data.members && Array.isArray(data.members)) {
        members = data.members;
    } else if (data.members) {
        members = Object.values(data.members);
    } else if (data.users) {
        members = Object.values(data.users);
    } else if (Array.isArray(data)) {
        members = data;
    } else if (data.data && Array.isArray(data.data)) {
        members = data.data;
    }
    
    return members.map((member) => {
        // Check if this is the new format (with networks array) or old format
        if (member.networks && Array.isArray(member.networks)) {
            // New format - same as formatUsers
            const formattedMember = {
                id: member.id,
                first_name: member.first_name,
                last_name: member.last_name,
                email: member.email,
                phone: member.phone || '',
                status: normalizeBooleanStatus(member.is_active ?? member.status),
                is_superuser: member.is_superuser,
                profile_image: member.profile_image,
                created_at: member.created_at,
                updated_at: member.updated_at,
                networks: member.networks || []
            };
            
            // If user has networks, use the first one for role info
            if (member.networks && member.networks.length > 0) {
                const firstNetwork = member.networks[0];
                formattedMember.role = {
                    id: firstNetwork.role_id,
                    name: firstNetwork.role_name
                };
                formattedMember.tenant_name = firstNetwork.tenant_name;
                formattedMember.membership_status = firstNetwork.status;
            }
            
            return formattedMember;
        } else {
            // Old format - legacy support (this is what we're getting from network members API)
            const formattedMember = {
                id: member.user_id,
                first_name: member.full_name ? member.full_name.split(' ')[0] : '',
                last_name: member.full_name ? member.full_name.split(' ').slice(1).join(' ') : '',
                email: member.email || '',
                phone: member.phone || '',
                status: member.status === 'approved' ? true : false, // Convert status to boolean
                is_superuser: false, // Network members don't have superuser info
                membership_status: member.status,
                role: {
                    id: member.role_id,
                    name: member.role_name
                },
                tenant_name: member.tenant_name,
                profile_image: member.profile_image,
                created_at: member.created_at,
                updated_at: member.updated_at,
                // Create networks array structure for compatibility with UserPage and EditUserPage
                networks: [{
                    tenant_id: member.tenant_id,
                    tenant_name: member.tenant_name,
                    tenant_subdomain: member.tenant_subdomain || '',
                    role_id: member.role_id,
                    role_name: member.role_name,
                    status: member.status
                }]
            };
            
            return formattedMember;
        }
    });
}

const formatUser = (response) => {
    let user = response.data.data.user;
    const roles = response.data.data.roles || {};
    const images = response.data.data.images || {};

    user = updateProperty(user, "profile_image_id", "profile_image", images);
    user = updateProperty(user, "role_id", "role", roles);
    return user
}

class UserApi {
    constructor(token) {
        this.api = new API(token);
        this.baseUrl = `${REACT_APP_API_URI}/admin/tenant`
        this.usersBaseUrl = `${REACT_APP_API_URI}/admin/users` // General users endpoint
    }

    // Get user by ID - Note: Direct endpoint may not be available
    getById(userId) {
        try {
            return this.api.get(`${this.usersBaseUrl}/${userId}`)
            .then((response) => {
                const formattedUser = formatUser(response);
                return formattedUser;
            })
            .catch((error) => {
                console.error('UserApi.getById - Direct endpoint not available:', error.message);
                throw new Error(`User endpoint not available. Cannot fetch user ${userId} directly.`);
            });
        } catch(error) {
            console.error('UserApi.getById - Error:', error);
            throw error;
        }
    }

    // Get all users (not tenant-specific)
    all(maxResults = 500) {
        try {
            return this.api.get(`${this.usersBaseUrl}?max_results=${maxResults}`)
            .then((response) => {
                const formattedUsers = formatUsers(response);
                return {
                    users: formattedUsers,
                    total_count: response.data.data?.total_count || formattedUsers.length,
                }
            })
            .catch((error) => {
                console.error('UserApi.all - API Error:', error);
                throw error;
            });
        }catch(error) {
            console.error('UserApi.all - Error:', error);
            return {
                users: [],
                total_count: 0,
            }
        }
    }

    // Get all tenant members - try different approaches
    getMembers(tenantId) {
        try {
            const headers = {
                'X-Tenant-ID': tenantId
            };
            
            // First try the original endpoint
            return this.api.get(`${this.baseUrl}/${tenantId}/members`, headers)
            .then((response) => {
                // Check if we got actual data
                if (response.data.members || (response.data.data && response.data.data.members)) {
                    const formattedMembers = formatMembers(response);
                    return {
                        members: formattedMembers,
                        total_count: response.data.data?.total_count || formattedMembers.length,
                    };
                } else {
                    // If no members data, try getting all users and filtering by network
                    return this.all().then((allUsersResponse) => {
                        const networkUsers = allUsersResponse.users.filter(user => 
                            user.networks && user.networks.some(network => network.tenant_id === parseInt(tenantId))
                        );
                        return {
                            members: networkUsers,
                            total_count: networkUsers.length,
                        };
                    });
                }
            })
            .catch((error) => {
                console.error('UserApi.getMembers - API Error:', error);
                throw error;
            });
        }catch(error) {
            console.error('UserApi.getMembers - Error:', error);
            return {
                members: [],
                total_count: 0,
            }
        }
    }

    // Get pending members
    getPendingMembers(tenantId) {
        try {
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.get(`${this.baseUrl}/${tenantId}/members/pending`, headers)
            .then((response) => {
                return {
                    pendingMembers: formatMembers(response),
                    total_count: response.data.data.total_count || 0,
                }
            });
        }catch(error) {
            console.error(error);
            return {
                pendingMembers: [],
                total_count: 0,
            }
        }
    }

    // Add user to tenant
    addMember(tenantId, userId, roleId) {
        try{
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.post(`${this.baseUrl}/${tenantId}/members`, {
                user_id: userId,
                role_id: roleId
            }, 'application/json', headers)
            .then((response) => {
                return response.data.data;
            });
        }catch(error) {
            console.error(`Error adding member to tenant. tenantId=${tenantId}, userId=${userId}, error=${error}`);
            return {}
        }
    }

    // Update user's role
    updateMemberRole(tenantId, userId, roleId) {
        try{
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.patch(`${this.baseUrl}/${tenantId}/members/${userId}`, {
                role_id: roleId
            }, 'application/json', headers)
            .then((response) => {
                return response.data.data;
            });
        }catch(error) {
            console.error(`Error updating member role. tenantId=${tenantId}, userId=${userId}, error=${error}`);
            return {}
        }
    }

    // Remove user from tenant
    removeMember(tenantId, userId) {
        try{
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.delete(`${this.baseUrl}/${tenantId}/members/${userId}`, headers)
            .then((response) => {
                return response.data.data;
            });
        }catch(error) {
            console.error(`Error removing member from tenant. tenantId=${tenantId}, userId=${userId}, error=${error}`);
            return {}
        }
    }

    // Approve pending member
    approveMember(tenantId, userId) {
        try{
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.post(`${this.baseUrl}/${tenantId}/members/${userId}/approve`, {}, 'application/json', headers)
            .then((response) => {
                return response.data.data;
            });
        }catch(error) {
            console.error(`Error approving member. tenantId=${tenantId}, userId=${userId}, error=${error}`);
            return {}
        }
    }

    // Reject pending member
    rejectMember(tenantId, userId) {
        try{
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.post(`${this.baseUrl}/${tenantId}/members/${userId}/reject`, {}, 'application/json', headers)
            .then((response) => {
                return response.data.data;
            });
        }catch(error) {
            console.error(`Error rejecting member. tenantId=${tenantId}, userId=${userId}, error=${error}`);
            return {}
        }
    }

    // Get available tenant roles
    getRoles(tenantId) {
        try {
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.get(`${this.baseUrl}/${tenantId}/roles`, headers)
            .then((response) => {
                return Object.values(response.data.data.roles || {});
            });
        }catch(error) {
            console.error(`Error fetching roles for tenant. tenantId=${tenantId}, error=${error}`);
            return []
        }
    }

    // Send password reset email via admin endpoint
    sendResetEmail(userId) {
        try{
            return this.api.post(`${this.usersBaseUrl}/${userId}/send_reset_email`, {}, 'application/json')
            .then((response) => {
                return response.data;
            });
        }catch(error) {
            console.error(`Error sending reset email for userId=${userId}, error=${error}`);
            throw error;
        }
    }

    // Send password reset email to a user within a tenant using email
    sendPasswordReset(tenantId, email) {
        try {
            const headers = {
                'X-Tenant-ID': tenantId
            };
            return this.api.post(`${this.baseUrl}/${tenantId}/send-password`, {
                email: email
            }, 'application/json', headers)
            .then((response) => {
                return response.data;
            });
        } catch(error) {
            console.error(`Error sending tenant password reset. tenantId=${tenantId}, email=${email}, error=${error}`);
            throw error;
        }
    }

    // Send password reset email via public endpoint using email (legacy)
    sendPasswordResetByEmail(email) {
        try{
            return this.api.post(`${REACT_APP_API_URI}/user/forgot-password`, {
                email: email
            }, 'application/json')
            .then((response) => {
                return response.data;
            });
        }catch(error) {
            console.error(`Error sending password reset for email=${email}, error=${error}`);
            return {}
        }
    }

    // Create a new user
    createUser(userData) {
        try {
            return this.api.post(`${this.usersBaseUrl}`, userData, 'application/json')
            .then((response) => {
                return response.data.data;
            });
        } catch(error) {
            console.error('Error creating user:', error);
            throw error;
        }
    }

    // Change user password
    changePassword(userId, newPassword) {
        try {
            return this.api.post(`${this.usersBaseUrl}/${userId}/password`, {
                password: newPassword
            }, 'application/json')
            .then((response) => {
                return response.data.data;
            });
        } catch(error) {
            console.error('Error changing user password:', error);
            throw error;
        }
    }

    // Update user
    updateUser(userId, userData) {
        try {
            return this.api.patch(`${this.usersBaseUrl}/${userId}`, userData, 'application/json')
            .then((response) => {
                return response.data.data;
            });
        } catch(error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    // Deactivate user (soft delete)
    deactivateUser(userId) {
        try {
            return this.api.post(`${this.usersBaseUrl}/${userId}/deactivate`, {}, 'application/json')
            .then((response) => {
                return response.data?.data || response.data;
            });
        } catch(error) {
            console.error(`Error deactivating userId=${userId}:`, error);
            throw error;
        }
    }

    // Permanently delete user
    deleteUser(userId) {
        try {
            return this.api.delete(`${this.usersBaseUrl}/${userId}`)
            .then((response) => {
                return response.data?.data || response.data;
            });
        } catch(error) {
            console.error(`Error deleting userId=${userId}:`, error);
            throw error;
        }
    }

    // Network management aliases
    addToNetwork(tenantId, userId, roleId) {
        return this.addMember(tenantId, userId, roleId);
    }

    removeFromNetwork(tenantId, userId) {
        return this.removeMember(tenantId, userId);
    }
}

export default UserApi;
